import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { hasAdminAccess, readRememberedAudience, useCurrentRoles } from 'hooks/use-current-roles';
import { logout } from 'redux/auth/actions';
import { beginNotAdminExit, endNotAdminExit } from 'utils/axios-utils';
import { useAppDisPatch } from 'utils/common';
import Header from './components/header';
import Menu from './components/menu';
import { findNav, isPathAllowedFor } from './nav-config';
import styles from './styles.module.scss';
import SupportNotifier from 'pages/support-shared/support-notifier';
import LiveSupportNotifier from 'pages/live-support-manager/notifier';
import WatermarkOverlay from 'components/watermark';

interface Props {
  children: any;
}

const COLLAPSE_KEY = 'hp-admin-sider-collapsed';

/** Màn hẹp (điện thoại — vd mở từ app: Tôi → Trang quản trị): thanh bên thành ngăn kéo. */
const NARROW_QUERY = '(max-width: 768px)';

const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(NARROW_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return narrow;
};

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

// `/studio` (Studio của tôi) chỉ hiện trên menu của Quản lý Studio, nhưng mọi người đã đăng nhập đều mở
// được: trang tự chuyển Admin/SMOD sang /studios (SPEC Studio §8.1; SSO từ app có thể gửi `to: '/studio'`).
const STUDIO_MINE_PATH = '/studio';

/**
 * Trang có API chỉ cho ADMIN: MOD gõ thẳng URL sẽ thấy lời giải thích thay vì trang
 * trống + loạt lỗi. Chờ biết vai trò rồi mới render để trang không kịp gọi API.
 * Quản lý Studio (SPEC Studio §8.4) chỉ mở được mục khai `audience` có 'STUDIO'; "/" chuyển sang /studio.
 */
const RoleGate = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loaded, failed, isAdmin, audience, canImportData, canViewOnlineReward } =
    useCurrentRoles();
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const item = findNav(pathname)?.item;
  const adminOnly = !!item?.adminOnly;

  if (!loaded) {
    // Lần trước là Quản lý Studio → chờ ở mọi trang, để trang ngoài phạm vi không kịp gọi API.
    if (
      pathname === '/' ||
      adminOnly ||
      !!item?.audience ||
      readRememberedAudience() === 'STUDIO'
    ) {
      return (
        <div className={styles.gateLoading}>
          <Spin />
        </div>
      );
    }
    return children;
  }
  // auth/status lỗi (`failed`): không biết người xem → không chặn theo Studio / `audience`;
  // mục `adminOnly` giữ đúng cách chặn cũ ở cuối hàm.
  if (!failed && audience === 'STUDIO') {
    if (pathname === '/') return <Navigate to={STUDIO_MINE_PATH} replace />;
    if (
      !isPathAllowedFor(
        pathname,
        false,
        { dataImport: canImportData, onlineReward: canViewOnlineReward },
        'STUDIO'
      )
    ) {
      return (
        <Result
          status="403"
          title="Trang này không thuộc phạm vi Quản lý Studio"
          subTitle="Bạn dùng được mục Studio của tôi, Phạt và Hộp thư hỗ trợ."
          extra={
            <Button type="primary" onClick={() => navigate(STUDIO_MINE_PATH)}>
              Về Studio của tôi
            </Button>
          }
        />
      );
    }
    return children;
  }

  if (
    !failed &&
    item?.audience &&
    pathname !== STUDIO_MINE_PATH &&
    !item.audience.includes(audience ?? (isAdmin ? 'ADMIN' : 'MOD'))
  ) {
    return (
      <Result
        status="403"
        title="Bạn không có quyền mở trang này"
        extra={
          <Button type="primary" onClick={() => navigate('/')}>
            Về Tổng quan
          </Button>
        }
      />
    );
  }

  if (!adminOnly || isAdmin) return children;
  return (
    <Result
      status="403"
      title="Chức năng này chỉ dành cho Admin"
      subTitle="Tài khoản MOD không xem được dữ liệu ở trang này. Nhờ Admin thực hiện hoặc cấp quyền."
      extra={
        <Button type="primary" onClick={() => navigate('/')}>
          Về Tổng quan
        </Button>
      }
    />
  );
};

/**
 * Chặn theo vai trò lớp 2 (đặc tả §5): phiên còn sống nhưng tài khoản không còn ADMIN/MOD
 * (vd. Admin vừa gỡ quyền). Admin/MOD không phải chờ — vỏ admin render ngay, chỉ bị thay khi
 * đã biết chắc vai trò. auth/status lỗi (mạng chập chờn) thì KHÔNG chặn.
 *
 * Vỏ admin đã kịp gọi API không đủ quyền (401/403): beginNotAdminExit() bảo interceptor đứng yên
 * (không tải lại trang về /login, không ghi đè 'expired') cho tới khi đăng xuất + điều hướng xong.
 */
const AccessGate = ({ children }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDisPatch();
  const { loaded, failed, roles } = useCurrentRoles();
  const denied = loaded && !failed && !hasAdminAccess(roles);

  useEffect(() => {
    // false: interceptor đã tự rời phiên 'notAdmin' (tải lại về /login) — không đăng xuất lần hai.
    if (!denied || !beginNotAdminExit()) return;
    dispatch(logout(navigate, 'notAdmin')).finally(endNotAdminExit);
  }, [denied, dispatch, navigate]);

  if (denied) {
    return (
      <div className={styles.accessGate}>
        <Spin />
      </div>
    );
  }
  return children;
};

const Layout = ({ children }: Props) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const narrow = useIsNarrow();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Chọn trang trong ngăn kéo → đóng ngăn kéo.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const toggle = () => {
    if (narrow) {
      setDrawerOpen((open) => !open);
      return;
    }
    setCollapsed((value) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, value ? '0' : '1');
      } catch {
        // Không lưu được thì thôi — chỉ là tiện ích.
      }
      return !value;
    });
  };

  return (
    <AccessGate>
      {/* Tin mới gửi "HP Media Hỗ trợ" → popup góc màn hình máy tính + âm báo, ở mọi trang. */}
      <SupportNotifier />
      <LiveSupportNotifier />
      {/* Watermark chống lộ: chữ mờ tên + mã truy vết phủ mọi trang (components/watermark). */}
      <WatermarkOverlay />
      <div className={styles.layout}>
        <aside
          className={
            narrow
              ? `${styles.sidebar} ${styles.sidebarDrawer} ${
                  drawerOpen ? styles.sidebarDrawerOpen : ''
                }`
              : `${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`
          }
        >
          <Menu collapsed={narrow ? false : collapsed} />
        </aside>
        {narrow && drawerOpen && (
          <div className={styles.drawerBackdrop} onClick={() => setDrawerOpen(false)} aria-hidden />
        )}
        <div className={styles.boxMainContent}>
          <Header collapsed={narrow ? !drawerOpen : collapsed} onToggle={toggle} />
          <main className={styles.children}>
            <RoleGate>{children}</RoleGate>
          </main>
        </div>
      </div>
    </AccessGate>
  );
};

export default Layout;
